import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { getApiUrl } from '@/lib/subdomain'

const apiUrl = getApiUrl()

interface HeatmapDay {
  count: number
  platforms: string[]
}

interface HeatmapData {
  days: Record<string, HeatmapDay>
  startDate?: string
  endDate?: string
  rangeDays?: number
}

const CELL_SIZE = 13
const CELL_GAP = 3
const CELL_RADIUS = 2
const DAYS_IN_WEEK = 7
const LABEL_WIDTH = 32
const HEADER_HEIGHT = 20
const FOOTER_HEIGHT = 18

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', '']

const PLATFORM_COLORS: Record<string, string> = {
  steam: '#66c0f4',
  epic: '#a855f7',
  xbox: '#22c55e',
}

function getIntensityColor(count: number): string {
  if (count === 0) return 'rgba(255,255,255,0.03)'
  if (count <= 1) return 'rgba(34,211,238,0.2)'
  if (count <= 3) return 'rgba(34,211,238,0.4)'
  if (count <= 6) return 'rgba(34,211,238,0.6)'
  return 'rgba(34,211,238,0.85)'
}

function toDateOnly(dateStr: string): Date {
  return new Date(dateStr + 'T00:00:00')
}

function startOfWeekSunday(date: Date): Date {
  const d = new Date(date)
  d.setDate(d.getDate() - d.getDay())
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfWeekSaturday(date: Date): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + (6 - d.getDay()))
  d.setHours(0, 0, 0, 0)
  return d
}

function toIsoDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

function buildCalendarGrid(startDateStr?: string, endDateStr?: string): { grid: string[][]; today: string } {
  const now = new Date()
  const todayStr = toIsoDate(now)
  const endDate = endDateStr ? toDateOnly(endDateStr) : toDateOnly(todayStr)
  const startDate = startDateStr
    ? toDateOnly(startDateStr)
    : (() => {
      const d = new Date(endDate)
      d.setDate(d.getDate() - 540)
      return d
    })()

  const alignedStart = startOfWeekSunday(startDate)
  const alignedEnd = endOfWeekSaturday(endDate)

  const grid: string[][] = []
  const cursor = new Date(alignedStart)

  while (cursor <= alignedEnd) {
    const weekDays: string[] = []
    for (let day = 0; day < DAYS_IN_WEEK; day++) {
      const inRange = cursor >= startDate && cursor <= endDate
      weekDays.push(inRange ? toIsoDate(cursor) : '')
      cursor.setDate(cursor.getDate() + 1)
    }
    grid.push(weekDays)
  }

  return { grid, today: todayStr }
}

function getMonthBreaks(grid: string[][]): Array<{ label: string; x: number }> {
  const breaks: Array<{ label: string; x: number }> = []
  let lastMonth = -1
  let lastYear = -1

  for (let week = 0; week < grid.length; week++) {
    const firstDay = grid[week].find(d => d !== '')
    if (!firstDay) continue
    const parsed = new Date(firstDay + 'T00:00:00')
    const month = parsed.getMonth()
    const year = parsed.getFullYear()
    if (month !== lastMonth) {
      const yearLabel = String(year).slice(-2)
      const isNewYearBoundary = lastYear !== -1 && year !== lastYear
      breaks.push({
        label: isNewYearBoundary ? `${MONTH_LABELS[month]} '${yearLabel}` : MONTH_LABELS[month],
        x: week,
      })
      lastMonth = month
      lastYear = year
    }
  }

  return breaks
}

function getYearLabels(grid: string[][]): Array<{ label: string; x: number }> {
  const spans = new Map<number, { start: number; end: number }>()

  for (let week = 0; week < grid.length; week++) {
    const firstDay = grid[week].find(d => d !== '')
    if (!firstDay) continue
    const year = new Date(firstDay + 'T00:00:00').getFullYear()
    const existing = spans.get(year)
    if (!existing) {
      spans.set(year, { start: week, end: week })
    } else {
      existing.end = week
    }
  }

  return Array.from(spans.entries()).map(([year, span]) => ({
    label: String(year),
    x: (span.start + span.end) / 2,
  }))
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function GamingHeatmap() {
  const DAYS_BACK = 540
  const [tooltip, setTooltip] = useState<{
    x: number
    y: number
    date: string
    count: number
    platforms: string[]
  } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['activity-heatmap', DAYS_BACK],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/public/activity-heatmap?days=${DAYS_BACK}`)
      if (!res.ok) return { days: {} } as HeatmapData
      return res.json() as Promise<HeatmapData>
    },
    staleTime: 5 * 60 * 1000,
  })

  const { grid, today } = buildCalendarGrid(data?.startDate, data?.endDate)
  const monthBreaks = getMonthBreaks(grid)
  const yearLabels = getYearLabels(grid)
  const days = data?.days || {}

  const totalActivities = Object.values(days).reduce((sum, d) => sum + d.count, 0)
  const activeDays = Object.keys(days).length

  const svgWidth = LABEL_WIDTH + (grid.length * (CELL_SIZE + CELL_GAP))
  const svgHeight = HEADER_HEIGHT + (DAYS_IN_WEEK * (CELL_SIZE + CELL_GAP)) + FOOTER_HEIGHT

  return (
    <section className="py-20 px-4">
      <div className="container mx-auto">
        <h2 className="text-3xl font-display font-bold text-center mb-4">
          Gaming Activity
        </h2>
        <p className="text-gray-400 text-center mb-8">
          {isLoading ? 'Loading activity...' : (
            totalActivities > 0
              ? `${totalActivities} gaming events across ${activeDays} active days in the last ~${Math.round((data?.rangeDays || DAYS_BACK) / 30)} months`
              : 'Activity will populate as I play'
          )}
        </p>

        <div className="max-w-5xl mx-auto">
          <div className="mx-auto relative w-full">
            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="block mx-auto w-full h-auto"
            >
              {/* Month labels */}
              {monthBreaks.map((m, i) => (
                <text
                  key={i}
                  x={LABEL_WIDTH + m.x * (CELL_SIZE + CELL_GAP)}
                  y={12}
                  className="fill-gray-500"
                  fontSize={11}
                  fontFamily="inherit"
                >
                  {m.label}
                </text>
              ))}

              {/* Day labels */}
              {DAY_LABELS.map((label, i) => (
                label ? (
                  <text
                    key={i}
                    x={0}
                    y={HEADER_HEIGHT + i * (CELL_SIZE + CELL_GAP) + CELL_SIZE - 2}
                    className="fill-gray-500"
                    fontSize={10}
                    fontFamily="inherit"
                  >
                    {label}
                  </text>
                ) : null
              ))}

              {/* Cells */}
              {grid.map((week, weekIdx) =>
                week.map((dateStr, dayIdx) => {
                  if (!dateStr) return null
                  const isFuture = dateStr > today
                  const dayData = days[dateStr]
                  const count = isFuture ? 0 : (dayData?.count || 0)
                  const x = LABEL_WIDTH + weekIdx * (CELL_SIZE + CELL_GAP)
                  const y = HEADER_HEIGHT + dayIdx * (CELL_SIZE + CELL_GAP)

                  return (
                    <rect
                      key={dateStr}
                      x={x}
                      y={y}
                      width={CELL_SIZE}
                      height={CELL_SIZE}
                      rx={CELL_RADIUS}
                      ry={CELL_RADIUS}
                      fill={isFuture ? 'rgba(255,255,255,0.01)' : getIntensityColor(count)}
                      stroke={isFuture ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)'}
                      strokeWidth={0.5}
                      className={isFuture ? '' : 'cursor-pointer transition-opacity hover:opacity-80'}
                      onMouseEnter={isFuture ? undefined : (e) => {
                        const rect = (e.target as SVGRectElement).getBoundingClientRect()
                        setTooltip({
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                          date: dateStr,
                          count,
                          platforms: dayData?.platforms || [],
                        })
                      }}
                      onMouseLeave={isFuture ? undefined : () => setTooltip(null)}
                    />
                  )
                })
              )}

              {/* Year labels */}
              {yearLabels.map((y) => (
                <text
                  key={y.label}
                  x={LABEL_WIDTH + y.x * (CELL_SIZE + CELL_GAP)}
                  y={svgHeight - 4}
                  textAnchor="middle"
                  className="fill-gray-500"
                  fontSize={10}
                  fontFamily="inherit"
                >
                  {y.label}
                </text>
              ))}
            </svg>

            {/* Tooltip */}
            {tooltip && (
              <div
                className="fixed z-50 pointer-events-none"
                style={{
                  left: tooltip.x,
                  top: tooltip.y - 8,
                  transform: 'translate(-50%, -100%)',
                }}
              >
                <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl">
                  <p className="font-semibold text-white mb-1">
                    {tooltip.count === 0
                      ? 'No activity'
                      : `${tooltip.count} event${tooltip.count !== 1 ? 's' : ''}`
                    }
                  </p>
                  <p className="text-gray-400">{formatDate(tooltip.date)}</p>
                  {tooltip.platforms.length > 0 && (
                    <div className="flex gap-1.5 mt-1.5">
                      {tooltip.platforms.map((p) => (
                        <span
                          key={p}
                          className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{
                            backgroundColor: `${PLATFORM_COLORS[p] || '#666'}20`,
                            color: PLATFORM_COLORS[p] || '#999',
                          }}
                        >
                          {p === 'epic' ? 'fortnite' : p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
            <span>Less</span>
            {[0, 1, 2, 3, 4].map((level) => (
              <div
                key={level}
                className="rounded-sm"
                style={{
                  width: CELL_SIZE - 2,
                  height: CELL_SIZE - 2,
                  backgroundColor: getIntensityColor(
                    level === 0 ? 0 : level === 1 ? 1 : level === 2 ? 2 : level === 3 ? 4 : 7
                  ),
                  border: '0.5px solid rgba(255,255,255,0.04)',
                }}
              />
            ))}
            <span>More</span>

            <span className="ml-4 border-l border-gray-700 pl-4 flex items-center gap-3">
              {Object.entries(PLATFORM_COLORS).map(([platform, color]) => (
                <span key={platform} className="flex items-center gap-1">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="capitalize">
                    {platform === 'epic' ? 'fortnite' : platform}
                  </span>
                </span>
              ))}
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
